import type { Lot } from '../../types/lot'
import type { WizardFarm } from '../../types/farmWizard'
import { getActiveTenantKey, getTenantScopedKey } from '../../offline/tenant'
import { isLotActiveForInteraction } from '../../utils/lotInteraction'
import { isHiddenFromCrew, isInactive, sortUnitsForCrew } from '../../utils/unitStatus'
import { isHatcheryLot } from '../../utils/hatchery'

// Crew Mode Field Bundle v0 PRD §3 items 1–4 (findings #51 / #52).
//
// One "¿Dónde estás?" location shared by the four streams. Lot streams
// (feed, mortality, sampling) choose a lot segment — the lot *and* the unit
// it is in — so D-1/D-3 hold by construction; water chooses a unit (D-2).
// Pure helpers here; `useCrewLocation` wires them to React.

export type CrewLocationStream = 'feed' | 'mortality' | 'weightSample' | 'water'

export type CrewLocationOption = {
  /** Stable key: the lot id for lot streams, the unit id for water. */
  key: string
  productionUnitId: string
  unitName: string
  lotId?: string
  lotKey?: string
  /** "Lot 1 · A3" for lot streams, "A3" for water. */
  label: string
  /** Hatchery v0 (#84): the lot is at the egg or yolk-sac alevin stage. */
  hatchery?: boolean
}

export const isLotStream = (stream: CrewLocationStream): boolean => stream !== 'water'

/**
 * Locations the operator can pick for a stream, in the office-defined order
 * (`metadata.crewOrder`, then unit name). Hidden and Inactiva units never
 * appear; lot streams list only Active lots placed in a unit.
 */
export const buildCrewLocationOptions = (args: {
  stream: CrewLocationStream
  farm: WizardFarm | undefined
  lots: Lot[]
}): CrewLocationOption[] => {
  const { farm } = args
  if (!farm || isInactive(farm)) return []
  const units = sortUnitsForCrew(
    (farm.productionUnits ?? []).filter((unit) => unit?.id && !isHiddenFromCrew(unit) && !isInactive(unit))
  )

  if (!isLotStream(args.stream)) {
    return units.map((unit) => ({
      key: String(unit.id),
      productionUnitId: String(unit.id),
      unitName: String(unit.name ?? unit.id),
      label: String(unit.name ?? unit.id)
    }))
  }

  const options: CrewLocationOption[] = []
  for (const unit of units) {
    const unitLots = args.lots
      .filter((lot) => lot.productionUnitId === unit.id && isLotActiveForInteraction(lot.stage) && !(lot as { deletedAt?: string }).deletedAt)
      // Hatchery v0 (#84): eggs and yolk-sac alevins are not fed or weighed.
      .filter((lot) => !(isHatcheryLot(lot) && (args.stream === 'feed' || args.stream === 'weightSample')))
      .sort((a, b) => String(a.lotKey ?? '').localeCompare(String(b.lotKey ?? ''), undefined, { numeric: true }))
    for (const lot of unitLots) {
      options.push({
        key: lot.id,
        productionUnitId: String(unit.id),
        unitName: String(unit.name ?? unit.id),
        lotId: lot.id,
        lotKey: lot.lotKey,
        label: `${lot.lotKey} · ${unit.name ?? unit.id}`,
        ...(isHatcheryLot(lot) ? { hatchery: true } : {})
      })
    }
  }
  return options
}

/** Groups for the picker: lot streams group consecutive options by lot name. */
export const groupCrewLocationOptions = (
  stream: CrewLocationStream,
  options: CrewLocationOption[]
): Array<{ heading: string | null; options: CrewLocationOption[] }> => {
  if (!isLotStream(stream)) return [{ heading: null, options }]
  const groups = new Map<string, CrewLocationOption[]>()
  for (const option of options) {
    const heading = option.lotKey ?? ''
    const list = groups.get(heading) ?? []
    list.push(option)
    groups.set(heading, list)
  }
  return Array.from(groups.entries()).map(([heading, list]) => ({ heading, options: list }))
}

export type RememberedCrewLocation = { productionUnitId: string; lotId?: string }

const REMEMBERED_BASE_KEY = 'crewModeLastLocation'
// Before the shared location each stream remembered its own unit (#52).
const LEGACY_STREAM_KEYS = [
  'crewModeFeedLastProductionUnit',
  'crewModeMortalityLastProductionUnit',
  'crewModeWeightSampleLastProductionUnit',
  'crewModeWaterLastProductionUnit'
]

const storage = (): Storage | null => {
  try {
    return globalThis.window?.localStorage ?? null
  } catch {
    return null
  }
}

export const readRememberedCrewLocation = (farmId: string): RememberedCrewLocation | null => {
  const store = storage()
  if (!store || !farmId) return null
  const tenantKey = getActiveTenantKey()
  try {
    const raw = store.getItem(getTenantScopedKey(`${REMEMBERED_BASE_KEY}:${farmId}`, tenantKey))
    if (raw) {
      const parsed = JSON.parse(raw) as RememberedCrewLocation
      if (parsed && typeof parsed.productionUnitId === 'string') return parsed
    }
    for (const legacy of LEGACY_STREAM_KEYS) {
      const unitId = store.getItem(getTenantScopedKey(`${legacy}:${farmId}`, tenantKey))
      if (unitId) return { productionUnitId: unitId }
    }
  } catch {
    // a remembered location is a convenience only
  }
  return null
}

export const writeRememberedCrewLocation = (farmId: string, location: RememberedCrewLocation): void => {
  const store = storage()
  if (!store || !farmId) return
  try {
    store.setItem(getTenantScopedKey(`${REMEMBERED_BASE_KEY}:${farmId}`, getActiveTenantKey()), JSON.stringify(location))
  } catch {
    // ignore
  }
}

/**
 * The remembered location as an option of this stream, or null (the picker
 * opens — never a silent default, findings #52). A lot stream prefers the
 * remembered lot, else the first option in the remembered unit; water uses
 * the remembered unit.
 */
export const resolveCrewLocation = (
  stream: CrewLocationStream,
  options: CrewLocationOption[],
  remembered: RememberedCrewLocation | null
): CrewLocationOption | null => {
  if (!remembered) return null
  if (isLotStream(stream) && remembered.lotId) {
    const byLot = options.find((option) => option.lotId === remembered.lotId)
    if (byLot) return byLot
  }
  return options.find((option) => option.productionUnitId === remembered.productionUnitId) ?? null
}

/** The next location after `current` in office order (no wrap-around), or null. */
export const nextCrewLocation = (
  options: CrewLocationOption[],
  current: CrewLocationOption | null
): CrewLocationOption | null => {
  if (!current) return null
  const index = options.findIndex((option) => option.key === current.key)
  if (index < 0 || index + 1 >= options.length) return null
  return options[index + 1]
}
